import { BatteryCharging, MapPin, Wallet, Zap } from 'lucide-react';
import { formatNumber, formatPrice } from '@/lib/formatters';

interface KeyFactsBarProps {
  price?: number | null;
  rangeKm?: number | null;
  batteryKwh?: number | null;
  dcKw?: number | null;
  dc1080Min?: number | null;
  consumptionWhKm?: number | null;
}

interface Fact {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Zap;
}

/**
 * De tal brugeren beslutter ud fra — samlet øverst på bilsiden.
 * Kompakt 2-kolonne-grid på mobil med ensartede kort og tal som hovedperson.
 */
export function KeyFactsBar({
  price,
  rangeKm,
  batteryKwh,
  dcKw,
  dc1080Min,
  consumptionWhKm,
}: KeyFactsBarProps) {
  const unknown = 'Ikke bekræftet';

  // km tilført pr. 10 min hurtigladning (grov beregning ud fra DC-effekt og forbrug)
  const kmPer10Min =
    dcKw && consumptionWhKm && consumptionWhKm > 0
      ? Math.round(((dcKw * (10 / 60)) * 1000) / consumptionWhKm)
      : null;

  const facts: Fact[] = [
    {
      label: 'Pris fra',
      value: price ? formatPrice(price) : unknown,
      icon: Wallet,
    },
    {
      label: 'Rækkevidde (WLTP)',
      value: rangeKm ? `${formatNumber(rangeKm)} km` : unknown,
      sub: consumptionWhKm ? `${consumptionWhKm} Wh/km` : undefined,
      icon: MapPin,
    },
    {
      label: 'Batteri',
      value: batteryKwh ? `${batteryKwh} kWh` : unknown,
      icon: BatteryCharging,
    },
    {
      label: 'Hurtigladning',
      value: dcKw ? `${dcKw} kW` : unknown,
      sub: dc1080Min
        ? `10–80 %: ${dc1080Min} min`
        : kmPer10Min
          ? `ca. ${kmPer10Min} km / 10 min`
          : undefined,
      icon: Zap,
    },
  ];

  return (
    <section aria-label="Nøgletal" className="mb-6 md:mb-8">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {facts.map((fact) => (
          <div
            key={fact.label}
            className="flex min-h-[92px] flex-col justify-between rounded-xl border border-border bg-card px-3 py-3 sm:px-4"
          >
            <div className="flex items-center gap-1.5">
              <fact.icon className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
              <p className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {fact.label}
              </p>
            </div>
            <div className="mt-1.5">
              <p className="font-display text-[22px] font-bold leading-tight tracking-tight text-foreground sm:text-2xl">
                {fact.value}
              </p>
              <p className="mt-0.5 h-4 text-[11px] leading-4 text-muted-foreground">
                {fact.sub ?? ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
